import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { LogOut, Fish, Beef, Ticket, Download, BarChart3, ClipboardList, Users } from "lucide-react";
import { SubpageHeader } from "../components/SubpageHeader";
import { Reveal } from "../components/Reveal";
import { AdminMetrics } from "./AdminMetrics";
import { AdminAdesoes } from "./AdminAdesoes";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminPage() {
    const [token, setToken] = useState(() => sessionStorage.getItem("admin_token"));
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [erro, setErro] = useState(null);
    const [dados, setDados] = useState(null);
    const [aCarregar, setACarregar] = useState(false);
    const [vista, setVista] = useState("confirmacoes");

    const entrar = async (e) => {
        e.preventDefault();
        setErro(null);
        try {
            const { data } = await axios.post(`${API}/auth/login`, { email, password });
            sessionStorage.setItem("admin_token", data.token);
            setToken(data.token);
        } catch (err) {
            const d = err.response?.data?.detail;
            setErro(typeof d === "string" ? d : "Credenciais inválidas");
        }
    };

    const carregar = async (t) => {
        setACarregar(true);
        try {
            const { data } = await axios.get(`${API}/confirmacoes`, { headers: { Authorization: `Bearer ${t}` } });
            setDados(data);
        } catch {
            sessionStorage.removeItem("admin_token");
            setToken(null);
        } finally {
            setACarregar(false);
        }
    };

    useEffect(() => {
        if (token) carregar(token);
    }, [token]);

    const sair = () => {
        sessionStorage.removeItem("admin_token");
        setToken(null);
        setDados(null);
    };

    const exportarCsv = () => {
        const cabecalho = ["Nº Interno", "Nº Bilhete (Ticket Corner)", "Nome", "Email", "Prato", "Data"];
        const linhas = dados.confirmacoes.map((c) => [
            c.numero_bilhete || "",
            c.bilhete || "",
            c.nome,
            c.email,
            c.prato,
            new Date(c.created_at).toLocaleDateString("pt-PT"),
        ]);
        const csv = [cabecalho, ...linhas]
            .map((l) => l.map((v) => `"${String(v).replace(/"/g, '""')}"`).join(";"))
            .join("\r\n");
        const blob = new Blob(["﻿" + csv], { type: "text/csv;charset=utf-8" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = "confirmacoes-fados-a-mesa.csv";
        a.click();
        URL.revokeObjectURL(url);
    };

    const peixe = dados?.confirmacoes.filter((c) => c.prato === "peixe").length || 0;
    const carne = dados?.confirmacoes.filter((c) => c.prato === "carne").length || 0;

    return (
        <div className="min-h-screen bg-[#070503] text-[#efe6d6] relative" data-testid="admin-page">
            <SubpageHeader hideLangs />
            <motion.main
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
                className="mx-auto max-w-[1000px] px-5 sm:px-10 pt-36 pb-24"
            >
                {!token ? (
                    <Reveal className="max-w-md mx-auto">
                        <div className="text-center">
                            <h1 className="font-display font-medium gold-text text-3xl sm:text-4xl tracking-[0.08em]" data-testid="admin-login-title">
                                ÁREA DA ORGANIZAÇÃO
                            </h1>
                            <div className="flex justify-center mt-4">
                                <img src="/images/ornamento.webp" alt="" aria-hidden="true" className="w-32 opacity-90" />
                            </div>
                        </div>
                        <form onSubmit={entrar} className="border border-[#c9a24b]/30 bg-[#0a0705] p-8 mt-10 space-y-6" data-testid="admin-login-form">
                            <div>
                                <label htmlFor="admin-email" className="font-caps text-[10px] tracking-[0.3em] uppercase text-[#d19a3f] block mb-2.5">
                                    Email
                                </label>
                                <input
                                    id="admin-email"
                                    type="email"
                                    required
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    data-testid="admin-email"
                                    className="w-full bg-[#070503] border border-[#c9a24b]/30 px-4 py-3 font-light text-[#efe6d6] focus:border-[#c9a24b] focus:outline-none transition-colors duration-300"
                                />
                            </div>
                            <div>
                                <label htmlFor="admin-password" className="font-caps text-[10px] tracking-[0.3em] uppercase text-[#d19a3f] block mb-2.5">
                                    Palavra-passe
                                </label>
                                <input
                                    id="admin-password"
                                    type="password"
                                    required
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    data-testid="admin-password"
                                    className="w-full bg-[#070503] border border-[#c9a24b]/30 px-4 py-3 font-light text-[#efe6d6] focus:border-[#c9a24b] focus:outline-none transition-colors duration-300"
                                />
                            </div>
                            {erro && (
                                <p className="text-sm font-light text-[#e0a0a0]" data-testid="admin-erro">
                                    {erro}
                                </p>
                            )}
                            <button
                                type="submit"
                                data-testid="admin-login-submit"
                                className="w-full font-caps text-xs tracking-[0.3em] uppercase text-[#070503] bg-[#c9a24b] px-8 py-3.5 hover:bg-[#ecd49b] transition-colors duration-400"
                            >
                                Entrar
                            </button>
                        </form>
                    </Reveal>
                ) : (
                    <Reveal>
                        <div className="flex flex-wrap items-center justify-between gap-4">
                            <div className="flex flex-wrap items-center gap-6">
                                <h1 className="font-display font-medium gold-text text-3xl sm:text-4xl tracking-[0.08em]" data-testid="admin-lista-title">
                                    {vista === "confirmacoes" ? "CONFIRMAÇÕES" : vista === "adesoes" ? "ADESÕES" : "MÉTRICAS"}
                                </h1>
                                <div className="flex items-center gap-2" data-testid="admin-tabs">
                                    <button
                                        onClick={() => setVista("confirmacoes")}
                                        data-testid="admin-tab-confirmacoes"
                                        className={`inline-flex items-center gap-2 font-caps text-[10px] tracking-[0.25em] uppercase px-4 py-2 border transition-colors duration-300 ${vista === "confirmacoes" ? "text-[#070503] bg-[#c9a24b] border-[#c9a24b]" : "text-[#c9a24b] border-[#c9a24b]/40 hover:bg-[#c9a24b]/10"}`}
                                    >
                                        <ClipboardList size={13} /> Confirmações
                                    </button>
                                    <button
                                        onClick={() => setVista("metricas")}
                                        data-testid="admin-tab-metricas"
                                        className={`inline-flex items-center gap-2 font-caps text-[10px] tracking-[0.25em] uppercase px-4 py-2 border transition-colors duration-300 ${vista === "metricas" ? "text-[#070503] bg-[#c9a24b] border-[#c9a24b]" : "text-[#c9a24b] border-[#c9a24b]/40 hover:bg-[#c9a24b]/10"}`}
                                    >
                                        <BarChart3 size={13} /> Métricas
                                    </button>
                                    <button
                                        onClick={() => setVista("adesoes")}
                                        data-testid="admin-tab-adesoes"
                                        className={`inline-flex items-center gap-2 font-caps text-[10px] tracking-[0.25em] uppercase px-4 py-2 border transition-colors duration-300 ${vista === "adesoes" ? "text-[#070503] bg-[#c9a24b] border-[#c9a24b]" : "text-[#c9a24b] border-[#c9a24b]/40 hover:bg-[#c9a24b]/10"}`}
                                    >
                                        <Users size={13} /> Adesões
                                    </button>
                                </div>
                            </div>
                            <div className="flex items-center gap-3">
                                {vista === "confirmacoes" && (
                                    <button
                                        onClick={exportarCsv}
                                        disabled={!dados || dados.total === 0}
                                        data-testid="admin-exportar-csv"
                                        className="inline-flex items-center gap-2 font-caps text-[10px] tracking-[0.25em] uppercase text-[#070503] bg-[#c9a24b] px-5 py-2.5 hover:bg-[#ecd49b] transition-colors duration-300 disabled:opacity-40 disabled:cursor-not-allowed"
                                    >
                                        <Download size={14} /> Exportar CSV
                                    </button>
                                )}
                                <button
                                    onClick={sair}
                                    data-testid="admin-logout"
                                    className="inline-flex items-center gap-2 font-caps text-[10px] tracking-[0.25em] uppercase text-[#c9a24b] border border-[#c9a24b]/50 px-5 py-2.5 hover:bg-[#c9a24b] hover:text-[#070503] transition-colors duration-300"
                                >
                                    <LogOut size={14} /> Sair
                                </button>
                            </div>
                        </div>

                        {vista === "metricas" ? (
                            <AdminMetrics token={token} />
                        ) : vista === "adesoes" ? (
                            <AdminAdesoes token={token} />
                        ) : aCarregar && !dados ? (
                            <p className="font-display italic text-[#efe6d6]/60 mt-12">A carregar confirmações...</p>
                        ) : dados ? (
                            <>
                                <div className="grid grid-cols-3 gap-4 mt-10">
                                    <div className="border border-[#c9a24b]/30 bg-[#0a0705] p-5 text-center" data-testid="admin-total">
                                        <Ticket size={20} strokeWidth={1.3} className="text-[#c9a24b] mx-auto" />
                                        <p className="font-display gold-text text-3xl mt-2">{dados.total}</p>
                                        <p className="font-caps text-[9px] tracking-[0.25em] uppercase text-[#efe6d6]/60 mt-1">Confirmadas</p>
                                    </div>
                                    <div className="border border-[#c9a24b]/30 bg-[#0a0705] p-5 text-center" data-testid="admin-peixe">
                                        <Fish size={20} strokeWidth={1.3} className="text-[#c9a24b] mx-auto" />
                                        <p className="font-display gold-text text-3xl mt-2">{peixe}</p>
                                        <p className="font-caps text-[9px] tracking-[0.25em] uppercase text-[#efe6d6]/60 mt-1">Peixe</p>
                                    </div>
                                    <div className="border border-[#c9a24b]/30 bg-[#0a0705] p-5 text-center" data-testid="admin-carne">
                                        <Beef size={20} strokeWidth={1.3} className="text-[#c9a24b] mx-auto" />
                                        <p className="font-display gold-text text-3xl mt-2">{carne}</p>
                                        <p className="font-caps text-[9px] tracking-[0.25em] uppercase text-[#efe6d6]/60 mt-1">Carne</p>
                                    </div>
                                </div>

                                <div className="mt-10 border border-[#c9a24b]/25 overflow-x-auto" data-testid="admin-tabela">
                                    <table className="w-full text-left">
                                        <thead>
                                            <tr className="border-b border-[#c9a24b]/25 bg-[#0a0705]">
                                                <th className="font-caps text-[10px] tracking-[0.2em] uppercase text-[#d19a3f] px-5 py-4">Nº Interno</th>
                                                <th className="font-caps text-[10px] tracking-[0.2em] uppercase text-[#d19a3f] px-5 py-4">Nº Bilhete</th>
                                                <th className="font-caps text-[10px] tracking-[0.2em] uppercase text-[#d19a3f] px-5 py-4">Nome</th>
                                                <th className="font-caps text-[10px] tracking-[0.2em] uppercase text-[#d19a3f] px-5 py-4">Email</th>
                                                <th className="font-caps text-[10px] tracking-[0.2em] uppercase text-[#d19a3f] px-5 py-4">Prato</th>
                                                <th className="font-caps text-[10px] tracking-[0.2em] uppercase text-[#d19a3f] px-5 py-4">Data</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {dados.confirmacoes.map((c) => (
                                                <tr key={c.id} className="border-b border-[#c9a24b]/10" data-testid={`admin-row-${c.numero_bilhete || c.id}`}>
                                                    <td className="px-5 py-3.5 font-caps text-xs text-[#ecd49b]">{c.numero_bilhete || "—"}</td>
                                                    <td className="px-5 py-3.5 font-caps text-xs text-[#c9a24b]">{c.bilhete || "—"}</td>
                                                    <td className="px-5 py-3.5 font-light text-sm text-[#efe6d6]/85">{c.nome}</td>
                                                    <td className="px-5 py-3.5 font-light text-sm text-[#efe6d6]/60">{c.email}</td>
                                                    <td className="px-5 py-3.5 font-light text-sm text-[#efe6d6]/85 capitalize">{c.prato}</td>
                                                    <td className="px-5 py-3.5 font-light text-xs text-[#efe6d6]/50">
                                                        {new Date(c.created_at).toLocaleDateString("pt-PT")}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                    {dados.total === 0 && (
                                        <p className="font-display italic text-[#efe6d6]/50 text-center py-10">Ainda não há confirmações.</p>
                                    )}
                                </div>
                            </>
                        ) : null}
                    </Reveal>
                )}
            </motion.main>
        </div>
    );
}
